/* Custom multilingual back-in-stock modal.
 * Replaces the Klaviyo onsite BIS popup (single-language) with a theme modal
 * that submits straight to Klaviyo's client API, so PL and CZ each get their
 * own translated UI while the existing "Subscribed to Back in Stock" flow
 * keeps triggering. Also stamps a `language` profile property so the flow
 * can branch into PL/CZ email variants.
 */
(function () {
	if (window.__productBisInit) return;
	window.__productBisInit = true;

	var MODAL_ID = "product-back-in-stock-modal";
	var REVISION = "2024-06-15";
	// Subscribed variant ids, kept for 30 days: after a restock notification
	// the Klaviyo subscription is consumed, so re-subscribing later is valid.
	var STORAGE_KEY = "gl_bis_subscribed";
	var STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;

	function readSubscribed() {
		try {
			var map = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
			var now = Date.now();
			var fresh = {};
			for (var id in map) {
				if (now - map[id] < STORAGE_TTL) fresh[id] = map[id];
			}
			return fresh;
		} catch (e) {
			return {};
		}
	}

	function isSubscribed(variantId) {
		return !!readSubscribed()[variantId];
	}

	function saveSubscribed(variantId) {
		try {
			var map = readSubscribed();
			map[variantId] = Date.now();
			localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
		} catch (e) {}
	}

	function getModal() {
		return document.getElementById(MODAL_ID);
	}

	function getCompanyId(modal) {
		var override = modal.getAttribute("data-bis-company-id");
		if (override) return override;
		// Klaviyo app embed loads klaviyo.js?company_id=XXXXXX
		var script = document.querySelector('script[src*="klaviyo"][src*="company_id="]');
		if (script) {
			try {
				var id = new URL(script.src).searchParams.get("company_id");
				if (id) return id;
			} catch (e) {}
		}
		return null;
	}

	function resetState(modal) {
		modal.querySelector("[data-bis-form]").hidden = false;
		modal.querySelector("[data-bis-success]").hidden = true;
		modal.querySelector("[data-bis-success-new]").hidden = false;
		modal.querySelector("[data-bis-success-already]").hidden = true;
		modal.querySelector("[data-bis-error-invalid]").hidden = true;
		modal.querySelector("[data-bis-error-generic]").hidden = true;
		var btn = modal.querySelector("[data-bis-submit]");
		btn.classList.remove("loading");
		btn.disabled = false;
	}

	// Two variant-id sources that go stale at different moments:
	// - the form input updates instantly on user interaction (Alpine), but as a
	//   PROPERTY, so the section morph doesn't touch it right away;
	// - the data-bis-variant-id attribute is rewritten by the section morph,
	//   but lags between the click and the morph landing.
	function resolveVariantId(trigger) {
		// For modal opening: interaction truth - input first.
		var form = trigger.closest("[data-product-form]");
		var input = form && form.querySelector('input[name="id"]');
		if (input && input.value) return input.value;
		return trigger.getAttribute("data-bis-variant-id") || "";
	}

	function labelVariantId(trigger) {
		// For label refresh (runs on morph mutations): morph truth - attribute
		// first. Reading the input here would see the pre-morph value and no
		// later mutation would correct the label.
		var attr = trigger.getAttribute("data-bis-variant-id");
		if (attr) return attr;
		return resolveVariantId(trigger);
	}

	// Swap the trigger label to "you're on the list" for variants the visitor
	// already subscribed to (and back, when switching to another variant).
	// variantId override: during theme:variant:change the form's live input
	// still holds the PREVIOUS variant, so the caller passes the new id.
	function refreshTriggers(scope, variantId) {
		var root = scope && scope.querySelectorAll ? scope : document;
		var triggers = root.querySelectorAll("[data-bis-trigger]");
		for (var i = 0; i < triggers.length; i++) {
			var trigger = triggers[i];
			var span = trigger.querySelector(".btn-state-ready span");
			var subscribedText = trigger.getAttribute("data-bis-subscribed-text");
			if (!span || !subscribedText) continue;
			if (!trigger.hasAttribute("data-bis-default-text")) {
				trigger.setAttribute("data-bis-default-text", span.textContent.trim());
			}
			var subscribed = isSubscribed(variantId || labelVariantId(trigger));
			var next = subscribed ? subscribedText : trigger.getAttribute("data-bis-default-text");
			// Only write when different - the observer below watches text
			// mutations, so an unconditional write would loop forever.
			if (span.textContent.trim() !== next) span.textContent = next;
		}
	}

	function showSuccess(modal, alreadySubscribed) {
		modal.querySelector("[data-bis-form]").hidden = true;
		modal.querySelector("[data-bis-success-new]").hidden = alreadySubscribed;
		modal.querySelector("[data-bis-success-already]").hidden = !alreadySubscribed;
		modal.querySelector("[data-bis-success]").hidden = false;
	}

	function unlockScroll() {
		// If the quickview drawer is still open beneath the modal, hand the
		// scroll lock back to it instead of unlocking the page.
		var openDrawer = document.querySelector(".quickview__modal.is-open [data-drawer-scrolls]");
		if (openDrawer) {
			document.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true, detail: openDrawer }));
		} else {
			document.dispatchEvent(new CustomEvent("theme:scroll:unlock", { bubbles: true }));
		}
	}

	function openModal(trigger) {
		var modal = getModal();
		if (!modal) return;

		var variantId = resolveVariantId(trigger);
		modal.querySelector("[data-bis-variant-input]").value = variantId;

		var label = modal.querySelector("[data-bis-product-label]");
		var title = trigger.getAttribute("data-bis-product-title") || "";
		var variantTitle = trigger.getAttribute("data-bis-variant-title") || "";
		label.textContent = variantTitle ? title + " — " + variantTitle : title;
		label.hidden = !title;

		resetState(modal);

		if (variantId && isSubscribed(variantId)) {
			showSuccess(modal, true);
		}

		var MicroModal = window.themeVendor && window.themeVendor.MicroModal;
		if (MicroModal) {
			MicroModal.show(MODAL_ID, {
				onClose: unlockScroll,
			});
		} else {
			modal.classList.add("is-open");
			modal.setAttribute("aria-hidden", "false");
		}

		document.dispatchEvent(
			new CustomEvent("theme:scroll:lock", { bubbles: true, detail: modal.querySelector("[data-modal-content]") })
		);

		var email = modal.querySelector('input[name="email"]');
		var formVisible = !modal.querySelector("[data-bis-form]").hidden;
		if (formVisible && email && !email.value) {
			setTimeout(function () {
				email.focus();
			}, 100);
		}
	}

	function closeModal(modal) {
		var MicroModal = window.themeVendor && window.themeVendor.MicroModal;
		if (MicroModal) {
			try {
				MicroModal.close(MODAL_ID);
			} catch (e) {
				modal.classList.remove("is-open");
				modal.setAttribute("aria-hidden", "true");
			}
		} else {
			modal.classList.remove("is-open");
			modal.setAttribute("aria-hidden", "true");
		}
		unlockScroll();
	}

	function subscribeBackInStock(companyId, email, variantId) {
		return fetch("https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" + encodeURIComponent(companyId), {
			method: "POST",
			headers: { "Content-Type": "application/json", revision: REVISION },
			body: JSON.stringify({
				data: {
					type: "back-in-stock-subscription",
					attributes: {
						profile: { data: { type: "profile", attributes: { email: email } } },
						channels: ["EMAIL"],
					},
					relationships: {
						variant: { data: { type: "catalog-variant", id: "$shopify:::$default:::" + variantId } },
					},
				},
			}),
		});
	}

	function setLanguageProperty(companyId, email, language) {
		// Fire-and-forget: the property only needs to exist by the time the
		// restock flow email sends, and a failure must not break the signup UX.
		return fetch("https://a.klaviyo.com/client/profiles/?company_id=" + encodeURIComponent(companyId), {
			method: "POST",
			headers: { "Content-Type": "application/json", revision: REVISION },
			body: JSON.stringify({
				data: { type: "profile", attributes: { email: email, properties: { language: language } } },
			}),
		}).catch(function () {});
	}

	// Pipeline updates the form's variant input without always re-rendering the
	// buttons HTML, so the trigger's baked variant attributes go stale on
	// variant change. Keep them in sync (the modal label reads them).
	document.addEventListener("theme:variant:change", function (e) {
		var variant = e.detail && e.detail.variant;
		if (!variant) return;
		var scope = e.target && e.target.querySelector ? e.target : document;
		var trigger = scope.querySelector("[data-bis-trigger]");
		if (trigger) {
			trigger.setAttribute("data-bis-variant-id", variant.id);
			if (trigger.hasAttribute("data-bis-variant-title")) {
				trigger.setAttribute("data-bis-variant-title", variant.title);
			}
			refreshTriggers(scope, String(variant.id));
		}
		// The live input (and any re-rendered button markup) settles after
		// this event - re-evaluate once it has, like the legacy rebind did.
		setTimeout(function () {
			refreshTriggers(document);
		}, 200);
	});

	// Triggers (re-)rendered by AJAX: quickview drawer, section updates.
	document.addEventListener("theme:section:load", function () {
		refreshTriggers(document);
	});
	document.addEventListener("theme:section:update", function () {
		refreshTriggers(document);
	});
	refreshTriggers(document);

	// On variant change Pipeline MORPHS the button in place (updates
	// attributes and inner text, stripping our custom attributes) instead of
	// replacing the element, and theme:variant:change never reaches document
	// listeners on this theme. So watch attribute/text/childList mutations
	// around the trigger and re-apply the label after each morph.
	// NOTE: refresh directly - no requestAnimationFrame. rAF never fires in
	// background tabs, which would leave the label stale until the next
	// foreground frame. The observer callback runs after the morph settles,
	// and the write-only-when-different guard in refreshTriggers prevents
	// mutation loops.
	new MutationObserver(function (mutations) {
		for (var i = 0; i < mutations.length; i++) {
			var m = mutations[i];
			if (m.type === "attributes") {
				if (m.target.matches && m.target.matches("[data-bis-trigger], [data-product-form] input[name='id']")) {
					refreshTriggers(document);
					return;
				}
			} else if (m.type === "characterData") {
				var parent = m.target.parentElement;
				if (parent && parent.closest && parent.closest("[data-bis-trigger]")) {
					refreshTriggers(document);
					return;
				}
			} else {
				var added = m.addedNodes;
				for (var j = 0; j < added.length; j++) {
					var node = added[j];
					if (node.nodeType !== 1) continue;
					if (
						(node.matches && node.matches("[data-bis-trigger]")) ||
						(node.querySelector && node.querySelector("[data-bis-trigger]"))
					) {
						refreshTriggers(document);
						return;
					}
				}
			}
		}
	}).observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ["data-bis-variant-id", "value"],
		characterData: true,
	});

	// Delegated so triggers inside AJAX-loaded quickview markup work too.
	document.addEventListener("click", function (e) {
		var trigger = e.target.closest("[data-bis-trigger]");
		if (trigger) {
			e.preventDefault();
			openModal(trigger);
			return;
		}
		// Close only when the clicked element ITSELF is a closer (overlay
		// backdrop, X button, success close). Clicks inside the dialog bubble
		// up through the overlay, so closest() here would close the modal on
		// any click - including the email input.
		if (e.target.matches && e.target.matches("[data-micromodal-close]")) {
			var modal = e.target.closest("[data-bis-modal]");
			// Only handle our own modal, and only when MicroModal didn't
			// already close it (it handles these clicks when it opened the modal).
			if (modal && modal.classList.contains("is-open")) {
				e.preventDefault();
				closeModal(modal);
			}
		}
	});

	document.addEventListener("keydown", function (e) {
		if (e.key !== "Escape") return;
		var modal = getModal();
		if (modal && modal.classList.contains("is-open")) closeModal(modal);
	});

	document.addEventListener("submit", function (e) {
		var form = e.target.closest("[data-bis-form]");
		if (!form) return;
		e.preventDefault();
		if (form.getAttribute("data-submitting") === "1") return;

		var modal = getModal();
		var invalidEl = modal.querySelector("[data-bis-error-invalid]");
		var genericEl = modal.querySelector("[data-bis-error-generic]");
		invalidEl.hidden = true;
		genericEl.hidden = true;

		var email = (form.querySelector('input[name="email"]').value || "").trim();
		var variantId = form.querySelector("[data-bis-variant-input]").value;
		var companyId = getCompanyId(modal);

		if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
			invalidEl.hidden = false;
			return;
		}
		if (!companyId || !variantId) {
			genericEl.hidden = false;
			return;
		}

		var btn = modal.querySelector("[data-bis-submit]");
		form.setAttribute("data-submitting", "1");
		btn.classList.add("loading");
		btn.disabled = true;

		subscribeBackInStock(companyId, email, variantId)
			.then(function (res) {
				// Success is a 202 with an empty body - never parse it.
				if (!res.ok) throw new Error("BIS subscribe failed: " + res.status);
				setLanguageProperty(companyId, email, modal.getAttribute("data-bis-language") || "pl");
				saveSubscribed(variantId);
				showSuccess(modal, false);
				refreshTriggers(document);
			})
			.catch(function () {
				genericEl.hidden = false;
			})
			.finally(function () {
				form.removeAttribute("data-submitting");
				btn.classList.remove("loading");
				btn.disabled = false;
			});
	});

	// --- Klaviyo onsite Back-in-Stock guard ---------------------------------
	// Klaviyo's onsite BIS auto-injects a "notify me" button on EVERY sold-out
	// product by hijacking the theme's native sold-out <button data-add-to-cart>:
	// it force-hides that button (inline display:none !important) and drops
	// #klaviyo-bis-button-container in its place. It has no knowledge of the
	// `nos` metafield, so products that should simply read "Sold out" wrongly
	// get a signup button. NOS products render an <a data-bis-trigger> instead
	// of a submit button, so Klaviyo finds no target there and this theme's own
	// modal is used - those are left untouched. Here we restore the native
	// button inside the product form; product-buttons.css hides Klaviyo's.
	function neutralizeKlaviyoBis() {
		var wraps = document.querySelectorAll(".product__submit__buttons");
		for (var i = 0; i < wraps.length; i++) {
			var wrap = wraps[i];
			if (!wrap.querySelector("#klaviyo-bis-button-container")) continue;
			var native = wrap.querySelector("button[data-add-to-cart]");
			// Clear only the inline display Klaviyo set - leave everything else.
			if (native && /display\s*:\s*none/i.test(native.getAttribute("style") || "")) {
				native.style.removeProperty("display");
			}
		}
	}

	function scheduleNeutralize() {
		neutralizeKlaviyoBis();
		// Klaviyo sometimes hides the native button a tick after injecting its
		// container, so re-run shortly after.
		setTimeout(neutralizeKlaviyoBis, 150);
	}

	// Fires the moment Klaviyo injects its container (initial load, and after
	// each variant morph re-renders the buttons). Reacts only to that specific
	// node so it stays quiet during unrelated DOM churn.
	new MutationObserver(function (mutations) {
		for (var i = 0; i < mutations.length; i++) {
			var added = mutations[i].addedNodes;
			for (var j = 0; j < added.length; j++) {
				var node = added[j];
				if (node.nodeType !== 1) continue;
				if (
					node.id === "klaviyo-bis-button-container" ||
					(node.querySelector && node.querySelector("#klaviyo-bis-button-container"))
				) {
					scheduleNeutralize();
					return;
				}
			}
		}
	}).observe(document.body, { childList: true, subtree: true });

	document.addEventListener("theme:variant:change", scheduleNeutralize);
	document.addEventListener("theme:section:load", scheduleNeutralize);
	document.addEventListener("theme:section:update", scheduleNeutralize);
	// Safety net if Klaviyo injected its button before this script ran.
	setTimeout(scheduleNeutralize, 1200);
	scheduleNeutralize();
})();
